import { Question } from '../types';

export type DifficultyTier = 'easy' | 'medium' | 'hard';

export interface AdaptiveSessionState {
  currentDifficulty: DifficultyTier;
  consecutiveCorrect: number;
  usedQuestionIds: Set<string>;
  domainStats: Record<string, { correct: number; total: number }>;
  sessionQuestions: Question[];
}

export function createInitialAdaptiveState(): AdaptiveSessionState {
  return {
    currentDifficulty: 'medium',
    consecutiveCorrect: 0,
    usedQuestionIds: new Set<string>(),
    domainStats: {},
    sessionQuestions: []
  };
}

/**
 * Calculates the next difficulty tier based on the 3-consecutive-correct / wrong-answer rules.
 */
export function calculateNextDifficulty(
  currentTier: DifficultyTier,
  consecutiveCorrect: number,
  wasCorrect: boolean
): { nextTier: DifficultyTier; nextStreak: number } {
  if (wasCorrect) {
    const updatedStreak = consecutiveCorrect + 1;
    if (updatedStreak >= 3) {
      // 3 consecutive correct -> bump to higher difficulty tier
      let nextTier: DifficultyTier = 'hard';
      if (currentTier === 'easy') nextTier = 'medium';
      else if (currentTier === 'medium') nextTier = 'hard';
      else nextTier = 'hard';

      return { nextTier, nextStreak: 0 };
    }
    return { nextTier: currentTier, nextStreak: updatedStreak };
  } else {
    // A wrong answer or struggling pattern -> lower difficulty tier
    let nextTier: DifficultyTier = 'easy';
    if (currentTier === 'hard') nextTier = 'medium';
    else if (currentTier === 'medium') nextTier = 'easy';
    else nextTier = 'easy';

    return { nextTier, nextStreak: 0 };
  }
}

/**
 * Identifies the weakest domain in the current session based on in-session accuracy.
 */
export function getWeakestInSessionDomain(
  domainStats: Record<string, { correct: number; total: number }>
): string | null {
  const entries = Object.entries(domainStats).filter(([_, stats]) => stats.total > 0);
  if (entries.length === 0) return null;

  // Find lowest accuracy (or lowest ratio with at least 1 mistake)
  let lowestAccuracy = 1.0;
  let weakestDomain: string | null = null;

  for (const [domain, stats] of entries) {
    const accuracy = stats.correct / stats.total;
    if (accuracy < lowestAccuracy && accuracy < 1.0) {
      lowestAccuracy = accuracy;
      weakestDomain = domain;
    }
  }

  return weakestDomain;
}

/**
 * Selects the next adaptive question from the pool without repeating any questions in the session.
 * Gracefully relaxes domain and difficulty constraints if the exact slice is exhausted.
 */
export function selectNextAdaptiveQuestion(
  allQuestions: Question[],
  usedQuestionIds: Set<string>,
  targetDifficulty: DifficultyTier,
  targetDomain: string | null = null
): Question | null {
  if (!allQuestions || allQuestions.length === 0) return null;

  // 1. Filter out all previously served questions in this session (No Repeats)
  const available = allQuestions.filter(q => !usedQuestionIds.has(q.id));
  if (available.length === 0) return null;

  // Helper for random pick among matching candidates
  const pickRandom = (list: Question[]): Question => {
    return list[Math.floor(Math.random() * list.length)];
  };

  // 2. Prioritize exact domain + exact target difficulty
  if (targetDomain) {
    const exactDomainAndDiff = available.filter(
      q => q.domain === targetDomain && (q.difficulty || 'medium') === targetDifficulty
    );
    if (exactDomainAndDiff.length > 0) {
      return pickRandom(exactDomainAndDiff);
    }

    // Relax difficulty within target domain
    const adjacentTiers: DifficultyTier[] = targetDifficulty === 'hard' 
      ? ['medium', 'easy'] 
      : targetDifficulty === 'easy' 
        ? ['medium', 'hard'] 
        : ['hard', 'easy'];

    for (const tier of adjacentTiers) {
      const match = available.filter(
        q => q.domain === targetDomain && (q.difficulty || 'medium') === tier
      );
      if (match.length > 0) return pickRandom(match);
    }

    // Any question in target domain
    const anyDomainMatch = available.filter(q => q.domain === targetDomain);
    if (anyDomainMatch.length > 0) return pickRandom(anyDomainMatch);
  }

  // 3. Match target difficulty across any domain
  const exactDiffAnyDomain = available.filter(
    q => (q.difficulty || 'medium') === targetDifficulty
  );
  if (exactDiffAnyDomain.length > 0) {
    return pickRandom(exactDiffAnyDomain);
  }

  // 4. Relax difficulty across all available questions
  const fallbackAdjacent: DifficultyTier[] = targetDifficulty === 'hard' 
    ? ['medium', 'easy'] 
    : targetDifficulty === 'easy' 
      ? ['medium', 'hard'] 
      : ['hard', 'easy'];

  for (const tier of fallbackAdjacent) {
    const match = available.filter(
      q => (q.difficulty || 'medium') === tier
    );
    if (match.length > 0) return pickRandom(match);
  }

  // 5. Absolute fallback: Any remaining unserved question
  return pickRandom(available);
}
